import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { MONTHS, startOfMonth } from '../events/calendar';
import { AnswersBadge } from '../requests/AnswersBadge';
import { HistoryDialog, QuestionHistory } from '../requests/QuestionHistory';
import { dayShortText, REQUESTS_KEY, shortDateTime, type QuestionsList } from '../requests/types';

/**
 * „Dostępność → Ludzie”, stage 2: every question and answer of the shown range in one place — who, which days, when
 * it was sent, what was answered (per day), whether it is stale („nieaktualne — zapytaj o nową datę”) and whether
 * „tak” put the person in the crew — and the confirmations sent to crews. Folded by default (fewer distractions).
 */
export function PeopleQuestions({ from, to }: { from: string; to: string }) {
  const list = useQuery({ queryKey: [...REQUESTS_KEY, 'list', from, to], placeholderData: keepPreviousData, queryFn: () => api<QuestionsList>(`/api/requests/list?from=${from}&to=${to}`) });
  const [history, setHistory] = useState(false);
  // owner 2026-10-30 21:49: only the questions still in force; the others are in „Historia”
  const all = list.data?.questions ?? [];
  const questions = all.filter((q) => q.active !== false);
  const confirmations = list.data?.confirmations ?? [];
  if (all.length === 0 && confirmations.length === 0) return null;
  return (
    <>
    {history && <PeopleHistory from={from} onClose={() => setHistory(false)} />}
    <details className="pg-questions" data-feature-id="requests.dates">
      <summary>
        Zapytania i odpowiedzi <span className="crew-count">{questions.length}</span>
        {confirmations.length > 0 ? ` · potwierdzenia do obsady: ${confirmations.length}` : ''}
      </summary>
      <div className="pg-q-tools">
        <button type="button" className="btn small" onClick={() => setHistory(true)} data-feature-id="requests.answers">
          Historia
        </button>
        {questions.length === 0 && <span className="muted">Brak aktualnych zapytań w tym okresie — wcześniejsze są w „Historii”.</span>}
      </div>
      {questions.length > 0 && (
        <ul className="pg-questions-list" aria-label="Zapytania i odpowiedzi">
          {questions.map((q) => (
            <li key={q.id} className={q.superseded ? 'superseded' : ''}>
              <span className="pg-q-who">{q.personName ?? 'Osoba'}</span>
              <span className="pg-q-what">
                {q.eventId ? <Link to={`/wydarzenia?wydarzenie=${q.eventId}&zakladka=obsada`}>{q.eventName}</Link> : (q.eventName ?? 'zapytanie o termin')}
                {q.days && q.days.length > 0 && <span className="muted"> — dni: {q.days.map((d) => dayShortText(d.day)).join(', ')}</span>}
              </span>
              <span className="muted">{q.sentAt ? `wysłano ${shortDateTime(q.sentAt)}` : ''}</span>
              {q.superseded ? <span className="muted">zastąpione nowym zapytaniem</span> : <AnswersBadge recipient={q} />}
              {q.added && !q.superseded && <span className="crew-confirmed">dodano do obsady</span>}
              {q.addRefused && !q.superseded && (q.assignDismissed ? (
                // owner 2026-10-29 00:32: „Zrezygnuj z przydziału” — the reason stays in the bubble
                <span className="muted" title={q.addRefused}>
                  zrezygnowano z przydziału
                </span>
              ) : (
                <span className="pg-q-assign">{q.addRefused}</span>
              ))}
            </li>
          ))}
        </ul>
      )}
      {confirmations.length > 0 && (
        <ul className="pg-questions-list" aria-label="Potwierdzenia do obsady" data-feature-id="requests.confirmation">
          {confirmations.map((c) => (
            <li key={c.id}>
              <span className="pg-q-who">Potwierdzenie do obsady</span>
              <span className="pg-q-what">{c.eventId ? <Link to={`/wydarzenia?wydarzenie=${c.eventId}&zakladka=obsada`}>{c.eventName}</Link> : c.eventName}</span>
              <span className="muted">
                {c.sentAt ? `wysłano ${shortDateTime(c.sentAt)}` : ''} ({[c.sentBy, `${c.people} ${c.people === 1 ? 'osoba' : 'osób'}`].filter(Boolean).join(', ')})
              </span>
            </li>
          ))}
        </ul>
      )}
    </details>
    </>
  );
}

/** „Historia” (owner 2026-10-30 21:49): every question of a month, inactive ones and deleted events too, read-only. */
function PeopleHistory({ from, onClose }: { from: string; onClose: () => void }) {
  const [month, setMonth] = useState(startOfMonth(from));
  const [y, m] = month.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
  const shift = (dir: 1 | -1) => setMonth(new Date(Date.UTC(y, m - 1 + dir, 1)).toISOString().slice(0, 10));
  const list = useQuery({ queryKey: [...REQUESTS_KEY, 'history', month], queryFn: () => api<QuestionsList>(`/api/requests/list?from=${month}&to=${last}&history=1`) });
  return (
    <HistoryDialog onClose={onClose} loading={list.isLoading} error={list.error ? (list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać historii') : null}>
      <div className="my-cal-nav">
        <button className="btn icon" type="button" aria-label="Poprzedni miesiąc" onClick={() => shift(-1)}>
          ‹
        </button>
        <button className="btn icon" type="button" aria-label="Następny miesiąc" onClick={() => shift(1)}>
          ›
        </button>
        <h3 aria-live="polite">
          {MONTHS[m - 1]} {y}
        </h3>
      </div>
      {list.data && <QuestionHistory items={list.data.questions} />}
    </HistoryDialog>
  );
}
